import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { CommonDatePipe } from 'src/app/pipe/common-date.pipe';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import { IPatientDailyReport, PatientDailyReportContext, PatientDailyReportDialogResult, PatientDailyReportFormData, PatientDailyReportFormMode } from './patient-daily-report.types';
import { PatientDailyReportService } from './patient-daily-report.service';
import { PatientDailyReportComponent } from './patient-daily-report-detail/patient-daily-report.component';

@Component({
  selector: 'app-patient-daily-report-list',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    CommonDatePipe,
    TimeFormatPipe
  ],
  templateUrl: './patient-daily-report-list.component.html',
  styleUrl: './patient-daily-report-list.component.scss'
})
export class PatientDailyReportListComponent implements OnInit, OnDestroy {
  @Input() patientDetails?: PatientDailyReportContext['patientDetails'];
  @Input() opdDetails?: PatientDailyReportContext['opdDetails'] | null;
  @Input() ipdData?: PatientDailyReportContext['ipdData'] | null;
  @Input() doctorData?: PatientDailyReportContext['doctorData'] | null;
  @Input() patientId?: number;
  @Input() opdId?: number | null;
  @Input() ipdId?: number | null;
  @Input() ipdNumber?: string | null;
  @Input() doctorId?: number;
  @Input() readOnly = false;
  @Input() isDialog = false;
  @Output() reportsChanged = new EventEmitter<IPatientDailyReport[]>();

  displayedColumns = ['reportDate', 'reportTime', 'summary', 'medicines', 'actions'];
  reports: IPatientDailyReport[] = [];
  isLoading = false;
  private destroy$ = new Subject<void>();

  constructor(
    private reportService: PatientDailyReportService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    @Optional() private dialogRef: MatDialogRef<PatientDailyReportListComponent, IPatientDailyReport[]>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: PatientDailyReportContext
  ) {}

  ngOnInit(): void {
    this.applyDialogData();
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canEdit(): boolean {
    return !this.readOnly;
  }

  get hasReports(): boolean {
    return this.reports.length > 0;
  }

  get canViewReport(): boolean {
    return !!(this.ipdData?.dischargeDate && this.ipdData?.dischargeTime);
  }

  loadReports(): void {
    const patientId = this.getPatientId();
    if (!patientId) return;

    this.isLoading = true;
    this.reportService.list({
      patientId,
      opdId: this.opdId ?? this.opdDetails?.id ?? null,
      ipdId: this.ipdId ?? this.ipdData?.id ?? null
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.reports = response.result ?? [];
          this.reportsChanged.emit(this.reports);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
        }
      });
  }

  addReport(): void {
    if (!this.canEdit) return;
    this.openForm('create');
  }

  viewReport(report: IPatientDailyReport): void {
    this.openForm('view', report);
  }

  editReport(report: IPatientDailyReport): void {
    if (!this.canEdit) return;
    this.openForm('edit', report);
  }


  deleteReport(report: IPatientDailyReport): void {
    if (!this.canEdit || !report.id) return;

    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        message: `${this.translate.instant('DELETE_DIALOG')} ${this.translate.instant('ENTITY.DAY_REPORT')}?`
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== 'confirm') return;

      this.reportService.delete(report.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastr.success(this.translate.instant('COMMON.DELETE').replace('{0}', this.translate.instant('ENTITY.DAY_REPORT')));
            this.loadReports();
          },
          error: () => this.toastr.error(this.translate.instant('COMMON.ERROR'))
        });
    });
  }

  close(): void {
    this.dialogRef?.close(this.reports);
  }

  getSummary(report: IPatientDailyReport): string {
    return [
      report.dailynotes,
      report.plan
    ].find(value => !!value?.trim()) ?? this.translate.instant('LABEL.MEDICINE_ORDERS');
  }

  private openForm(mode: PatientDailyReportFormMode, report?: IPatientDailyReport) {
    const data: PatientDailyReportFormData = {
      ...this.buildContext(),
      mode,
      report,
      readOnly: this.readOnly || mode === 'view',
      isDialog: true
    };

    const dialogRef = this.dialog.open<PatientDailyReportComponent, PatientDailyReportFormData, PatientDailyReportDialogResult>(
      PatientDailyReportComponent,
      {
        position: { top: '0', right: '0' },
        height: '100vh',
        width: '82vw',
        maxWidth: '1400px',
        panelClass: 'side-panel',
        disableClose: true,
        data
      }
    );

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result?.saved) {
        this.loadReports();
      }
    });

    return dialogRef;
  }

  private applyDialogData(): void {
    if (!this.data) return;
    this.patientDetails = this.patientDetails ?? this.data.patientDetails;
    this.opdDetails = this.opdDetails ?? this.data.opdDetails;
    this.ipdData = this.ipdData ?? this.data.ipdData;
    this.doctorData = this.doctorData ?? this.data.doctorData;
    this.patientId = this.patientId ?? this.data.patientId ?? this.data.patientDetails?.id;
    this.opdId = this.opdId ?? this.data.opdId ?? this.data.opdDetails?.id ?? null;
    this.ipdId = this.ipdId ?? this.data.ipdId ?? this.data.ipdData?.id ?? null;
    const ipdNumber = this.ipdNumber ?? this.data.ipdNumber ?? this.data.ipdData?.ipdNumber ?? null;
    this.ipdNumber = ipdNumber === null || ipdNumber === undefined ? null : String(ipdNumber);
    this.doctorId = this.doctorId ?? this.data.doctorId ?? this.data.doctorData?.id ?? this.data.opdDetails?.doctorId ?? this.data.ipdData?.doctorId;
    this.readOnly = this.readOnly || !!this.data.readOnly;
    this.isDialog = this.isDialog || !!this.data.isDialog;
  }

  private buildContext(): PatientDailyReportContext {
    return {
      patientDetails: this.patientDetails,
      opdDetails: this.opdDetails ?? undefined,
      ipdData: this.ipdData ?? undefined,
      doctorData: this.doctorData ?? undefined,
      patientId: this.getPatientId(),
      opdId: this.opdId ?? this.opdDetails?.id ?? null,
      ipdId: this.ipdId ?? this.ipdData?.id ?? null,
      ipdNumber: this.normalizeIpdNumber(this.ipdNumber ?? this.ipdData?.ipdNumber ?? null),
      doctorId: this.doctorId ?? this.doctorData?.id ?? this.opdDetails?.doctorId ?? this.ipdData?.doctorId,
      readOnly: this.readOnly,
      isDialog: true
    };
  }

  private getPatientId(): number {
    return Number(this.patientId ?? this.patientDetails?.id ?? this.opdDetails?.patientId ?? 0);
  }

  private normalizeIpdNumber(value: string | number | null | undefined): string | null {
    return value === null || value === undefined ? null : String(value);
  }
}
